/**
 * DocumentationModal Component
 * Displays quick help text and link to full documentation
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiX, FiExternalLink, FiBook } from 'react-icons/fi';
import documentationService from '../../services/DocumentationService';

const DocumentationModal = ({ documentationId, onClose }) => {
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const loadDocumentation = async () => {
            try {
                if (!documentationService.initialized) {
                    await documentationService.initialize();
                }

                const docEntry = documentationService.getEntry(documentationId);
                setEntry(docEntry);
            } catch (error) {
                console.error('Failed to load documentation:', error);
            } finally {
                setLoading(false);
            }
        };

        loadDocumentation();
    }, [documentationId]);

    useEffect(() => {
        // Handle escape key to close modal
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        // Prevent body scroll when modal is open
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = originalOverflow;
        };
    }, [onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        }
    };

    const handleViewFullDocumentation = () => {
        if (entry) {
            documentationService.trackUsage(documentationId, 'fullDoc');

            // Close the modal first
            onClose();

            const docUrl = entry.documentationUrl;

            // Check if it's an external URL
            if (docUrl.startsWith('http://') || docUrl.startsWith('https://')) {
                // External link - open in new tab
                window.open(docUrl, '_blank', 'noopener,noreferrer');
            } else if (docUrl.startsWith('#')) {
                // Anchor link - scroll to section on current page
                const element = document.querySelector(docUrl);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            } else {
                // Internal route - use React Router navigation
                navigate(docUrl);
            }
        }
    };

    // Prevent the underlying UI from receiving the click when closing
    const handleCloseClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
    };

    // Intercept clicks on links inside quickHelp so internal routes use SPA navigation
    const handleQuickHelpClick = (e) => {
        const anchor = e.target.closest('a');
        if (!anchor) return;
        const href = anchor.getAttribute('href');
        if (!href) return;
        // Internal route
        if (href.startsWith('/')) {
            e.preventDefault();
            // Close the modal before navigating
            onClose();
            navigate(href);
        }
        // Otherwise let default behavior occur (e.g., http links)
    };

    const modalContent = (() => {
        if (loading) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 shadow-xl">
                        <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-gray-600">Loading documentation...</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (!entry) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 shadow-xl max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Documentation Not Found</h3>
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Close"
                            >
                                <FiX size={20} />
                            </button>
                        </div>
                        <p className="text-gray-600">
                            No documentation available for this feature yet.
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                onClick={handleBackdropClick}
            >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-in fade-in duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                            <FiBook className="text-blue-600" size={20} />
                            <h3 className="text-lg font-semibold text-gray-900">
                                {entry.title}
                            </h3>
                        </div>
                        <button
                            onMouseDown={handleCloseClick}
                            className="text-gray-500 hover:text-gray-700 transition-colors rounded-full p-1 hover:bg-gray-100"
                            aria-label="Close"
                        >
                            <FiX size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        <div
                            className="text-gray-700 leading-relaxed mb-3"
                            onClick={handleQuickHelpClick}
                            dangerouslySetInnerHTML={{ __html: entry.quickHelp }}
                        />

                        <button
                            onClick={handleViewFullDocumentation}
                            className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors underline-offset-2 hover:underline"
                        >
                            <span>View documentation</span>
                            <FiExternalLink size={12} />
                        </button>
                    </div>
                </div>
            </div>
        );
    })();

    return createPortal(modalContent, document.body);
};

export default DocumentationModal; 